class StepController {
  constructor(settings = {}) {
    this.settings = settings;
    this.steps = new Map();  // Stores step name → DOM element
    this.queue = []; // Reserved for future use
    this.active = null;
  }

  // Registers an event listener for a specific step event.
  // Supported events: "show", "load".
  // Multiple listeners can be registered for each event.
  setListener(event, fn) {
    const map = { show: "onShow", load: "onLoad", error: "onError", update: "onUpdate" };
    const key = map[event];
    if (!key || typeof fn !== "function") return;
  
    this.settings._listeners ??= { onShow: [], onLoad: [], onError: [], onUpdate: [] };
    this.settings._listeners[key].push(fn);
  }  

  // Loads a step from an external URL, extracts the desired selector,
  // and adds it as a new step. Retries up to 5 times if fetch fails.
  async load(name, url, selector, attempts = 0) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Fetch failed");

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      // Remove external scripts and styles for security and isolation
      doc.querySelectorAll('script[src], link[rel="stylesheet"][href]').forEach(el => el.remove());

      const el = doc.querySelector(selector);
      if (!el) throw new Error("Selector not found");

      this.add(name, el.cloneNode(true));

      try {
        const step = this.steps.get(name);
        this.settings.onLoad?.(name, step);
      } catch (e) {
        this.settings.onError?.(e);
      }
      this.settings._listeners?.onLoad?.forEach(fn => {
        try {
          fn(name, step);
        } catch (e) {
          this.settings.onError?.(e);
        }
      });        
    } catch (e) {
      if (attempts < 5) {
        const delay = 100 + Math.random() * 200;
        setTimeout(() => this.load(name, url, selector, attempts + 1), delay);
      } else {
        this.settings.onError?.(e);        
      }
    }
  }

  // Adds a named step to the internal map
  add(name, node) {
    this.steps.set(name, node);
  }

  
  update(name) {
    this.settings._listeners?.onUpdate?.forEach(fn => {
      try {
        fn(name);
      } catch (e) {
        this.settings.onError?.(e);
      }
    }); 
  }

  // Shows a step by name, inserts it into the DOM if needed,
  // and hides all other steps.
  show(name) {
    const step = this.steps.get(name);
    if (!step) return;

    const slot = document.querySelector(`[data-step="${name}"]`);
    const container = this.settings.container;

    if (slot) {
      slot.replaceWith(step);
    } else if (!step.isConnected && container) {
      container.appendChild(step);
    }

    step.style.display = "";
    this.active = name;

    try {
      this.settings.onShow?.(name, step);
    } catch (e) {
      this.settings.onError?.(e);
    }
    this.settings._listeners?.onShow?.forEach(fn => {
      try {
        fn(name, step);
      } catch (e) {
        this.settings.onError?.(e);
      }
    });      

    // Hide all other steps
    for (let [key, el] of this.steps) {
      if (key !== name) el.style.display = "none";
    }
  }
}

export default StepController;
